import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { coursesApi } from '@/lib/api';
import { formatPrice } from '@/lib/utils';
import { useState, useEffect } from 'react';

/* ═══ V9 v2 SVG Icons ═══ */
function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    sun: <svg {...p}><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>,
    arrow: <svg {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
    play: <svg {...p}><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill={color} stroke="none" /></svg>,
    check: <svg {...p}><polyline points="20 6 9 17 4 12" /></svg>,
    users: <svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>,
    target: <svg {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
    star: <svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill={color} stroke="none" /></svg>,
    grad: <svg {...p}><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 2 3 3 6 3s6-1 6-3v-5" /></svg>,
    clock: <svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    clip: <svg {...p}><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M9 14l2 2 4-4" /></svg>,
    chat: <svg {...p}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>,
    book: <svg {...p}><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

function IcBox({ type, color, bg, size = 48 }: { type: string; color: string; bg: string; size?: number }) {
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.26, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Ic type={type} size={size * 0.48} color={color} />
    </div>
  );
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};


function CertQuiz({ isRTL, S }: { isRTL: boolean; S: any }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);

  const questions = isRTL ? [
    { q: 'ما هو دورك الحالي؟', opts: ['تكنولوجيا المعلومات / الدعم الفني', 'إدارة / قيادة فريق', 'مجال آخر أو طالب'] },
    { q: 'كم سنة من الخبرة لديك في مجال IT؟', opts: ['لا خبرة سابقة', '1-3 سنوات', 'أكثر من 3 سنوات'] },
    { q: 'ما هو هدفك الرئيسي؟', opts: ['الحصول على شهادة معترف بها', 'تطوير مهاراتي الحالية', 'تغيير مساري المهني إلى IT'] },
    { q: 'هل سمعت بمفهوم إدارة خدمات IT (ITSM)؟', opts: ['نعم وأعمل به', 'سمعت به لكن لم أتعمق', 'لا، هذا جديد عليّ'] },
  ] : [
    { q: 'What is your current role?', opts: ['IT / Technical Support', 'Management / Team Lead', 'Other field or Student'] },
    { q: 'How much IT experience do you have?', opts: ['No prior experience', '1-3 years', 'More than 3 years'] },
    { q: 'What is your main goal?', opts: ['Get a recognized certification', 'Improve my current skills', 'Transition my career into IT'] },
    { q: 'Are you familiar with IT Service Management (ITSM)?', opts: ['Yes, I work with it', 'Heard of it but not deep', 'No, this is new to me'] },
  ];

  const handleAnswer = (optIndex: number) => {
    const newAnswers = [...answers, optIndex];
    setAnswers(newAnswers);
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      setShowResult(true);
    }
  };

  const reset = () => { setStep(0); setAnswers([]); setShowResult(false); };

  const getResult = () => {
    const hasIT = answers[0] === 0;
    const hasExp = answers[1] >= 1;
    const wantsCert = answers[2] === 0;
    const knowsITSM = answers[3] === 0;

    if (hasIT && hasExp && knowsITSM) return {
      match: 95,
      title: isRTL ? 'ITIL 5 Foundation مثالية لك!' : 'ITIL 5 Foundation is perfect for you!',
      desc: isRTL ? 'بخبرتك في IT ومعرفتك بإدارة الخدمات، هذه الشهادة ستعزز مسارك المهني بشكل كبير وتفتح أبواب الترقي.' : 'With your IT background and ITSM knowledge, this certification will significantly boost your career and open doors to advancement.'
    };
    if (hasIT || hasExp) return {
      match: 85,
      title: isRTL ? 'ITIL 5 Foundation خيار ممتاز!' : 'ITIL 5 Foundation is an excellent choice!',
      desc: isRTL ? 'خبرتك التقنية ستساعدك على استيعاب المفاهيم بسرعة. هذه الشهادة ستضيف بُعداً إدارياً قوياً لمهاراتك.' : 'Your technical background will help you grasp concepts quickly. This certification adds a strong management dimension to your skills.'
    };
    return {
      match: 75,
      title: isRTL ? 'ITIL 5 Foundation نقطة انطلاق رائعة!' : 'ITIL 5 Foundation is a great starting point!',
      desc: isRTL ? 'لا تحتاج خبرة سابقة! هذه الشهادة مصممة كمدخل لعالم إدارة خدمات IT وستمنحك أساساً قوياً لمسار مهني جديد.' : 'No prior experience needed! This certification is designed as an entry point to IT service management and gives you a solid foundation for a new career path.'
    };
  };

  const result = showResult ? getResult() : null;

  return (
    <section style={{ padding: '80px 24px', background: 'var(--soleil-linen)', borderTop: '1px solid var(--soleil-sand)' }}>
      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-gold-dark)', letterSpacing: '0.2em', marginBottom: 14 }}>
            {isRTL ? 'اكتشف مسارك' : 'FIND YOUR PATH'}
          </div>
          <h2 style={{ fontFamily: S.heading, fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 600, color: 'var(--soleil-ink)' }}>
            {isRTL ? 'هل شهادة ITIL مناسبة ' : 'Is ITIL certification right '}
            <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
              {isRTL ? 'لك؟' : 'for you?'}
            </span>
          </h2>
          <p style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-sub)', marginTop: 10 }}>
            {isRTL ? 'أجب على 4 أسئلة سريعة لمعرفة ذلك' : 'Answer 4 quick questions to find out'}
          </p>
        </div>

        <div style={{ background: '#fff', borderRadius: 20, border: '1px solid var(--soleil-sand)', padding: '36px 32px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--soleil-gold), var(--soleil-sienna))', opacity: 0.65 }} />

          {!showResult ? (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
                {questions.map((_, i) => (
                  <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? 'var(--soleil-gold)' : 'var(--soleil-sand)', transition: 'background 0.3s' }} />
                ))}
              </div>
              <div style={{ fontFamily: S.font, fontSize: 11, color: 'var(--soleil-mut)', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 10 }}>
                {isRTL ? `سؤال ${step + 1} من ${questions.length}` : `QUESTION ${step + 1} OF ${questions.length}`}
              </div>
              <h3 style={{ fontFamily: S.heading, fontSize: 20, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 22 }}>
                {questions[step].q}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {questions[step].opts.map((opt, i) => (
                  <button key={i} onClick={() => handleAnswer(i)} style={{ fontFamily: S.font, fontSize: 14, padding: '14px 20px', borderRadius: 12, border: '1px solid var(--soleil-sand)', background: 'var(--soleil-cream)', color: 'var(--soleil-ink)', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', fontWeight: 500, transition: 'all 0.2s' }}
                    onMouseEnter={e => { (e.target as HTMLElement).style.borderColor = 'var(--soleil-gold)'; (e.target as HTMLElement).style.background = 'var(--soleil-gold-pale)'; }}
                    onMouseLeave={e => { (e.target as HTMLElement).style.borderColor = 'var(--soleil-sand)'; (e.target as HTMLElement).style.background = 'var(--soleil-cream)'; }}
                  >{opt}</button>
                ))}
              </div>
            </>
          ) : result && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 88, height: 88, borderRadius: '50%', background: 'var(--soleil-gold-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28, fontWeight: 800, fontFamily: S.heading, color: 'var(--soleil-gold-dark)' }}>
                {result.match}%
              </div>
              <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-gold-dark)', letterSpacing: '0.15em', marginBottom: 8 }}>
                {isRTL ? 'نسبة التوافق' : 'MATCH SCORE'}
              </div>
              <h3 style={{ fontFamily: S.heading, fontSize: 22, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 12 }}>
                {result.title}
              </h3>
              <p style={{ fontFamily: S.font, fontSize: 14, color: 'var(--soleil-sub)', lineHeight: 1.7, marginBottom: 28, maxWidth: 460, margin: '0 auto 28px' }}>
                {result.desc}
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="/courses" style={{ fontFamily: S.font, fontSize: 14, fontWeight: 700, padding: '12px 32px', borderRadius: 50, background: 'var(--soleil-gold)', color: '#fff', textDecoration: 'none', letterSpacing: '0.02em' }}>
                  {isRTL ? 'ابدأ الآن' : 'Get Started'}
                </a>
                <button onClick={reset} style={{ fontFamily: S.font, fontSize: 14, fontWeight: 600, padding: '12px 24px', borderRadius: 50, background: 'transparent', border: '1px solid var(--soleil-sand)', color: 'var(--soleil-sub)', cursor: 'pointer' }}>
                  {isRTL ? 'أعد الاختبار' : 'Retake Quiz'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function SoleilHomePage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [show, setShow] = useState(false);

  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);

  // Feature cards scroll-in animation
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const cards = e.target.querySelectorAll(".feat-animate");
          cards.forEach((card) => {
            (card as HTMLElement).style.opacity = "1";
            (card as HTMLElement).style.transform = "translateY(0)";
            const bar = card.querySelector(".feat-bar") as HTMLElement;
            if (bar) bar.style.width = "100%";
          });
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.15 });
    const grid = document.querySelector(".soleil-grid-features");
    if (grid) obs.observe(grid);
    return () => obs.disconnect();
  }, []);

  // Testimonial cards scroll-in animation
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const cards = e.target.querySelectorAll(".test-animate");
          cards.forEach((card) => {
            (card as HTMLElement).style.opacity = "1";
            (card as HTMLElement).style.transform = "translateY(0)";
          });
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.15 });
    const grid = document.querySelector(".soleil-grid-testimonials");
    if (grid) obs.observe(grid);
    return () => obs.disconnect();
  }, []);

  const { data: courses } = useQuery({
    queryKey: ['courses', 'featured'],
    queryFn: () => coursesApi.list({ featured: true }),
  });

  const featuredCourse = courses?.[0];

  return (
    <div>
      {/* ═══ HERO — centered, marketing-focused ═══ */}
      <section style={{
        minHeight: '80vh', position: 'relative', overflow: 'hidden',
        background: 'var(--soleil-cream)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {/* Ambient light */}
        <div style={{ position: 'absolute', top: '-20%', right: '10%', width: '40%', height: '50%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(184,148,58,0.07), transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-15%', left: '20%', width: '30%', height: '40%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(184,92,58,0.04), transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none' }} />

        <div style={{
          maxWidth: 720, margin: '0 auto', padding: '0 24px', textAlign: 'center',
          position: 'relative', zIndex: 2,
        }}>
          {/* Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--soleil-gold-pale)', border: '1px solid var(--soleil-sand)', borderRadius: 100, padding: '7px 20px 7px 10px', marginTop: 20, marginBottom: 36, opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(20px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0s' }}>
            <Ic type="sun" size={14} color="var(--soleil-gold)" />
            <span style={{ fontFamily: S.font, fontSize: 12, fontWeight: 600, color: 'var(--soleil-gold-dark)' }}>
              {isRTL ? 'أكاديمية ITIL الرائدة في العراق' : "Iraq's Premier ITIL Academy"}
            </span>
          </div>

          <h1 style={{ fontFamily: S.heading, fontSize: 'clamp(36px, 5vw, 62px)', fontWeight: 600, color: 'var(--soleil-ink)', lineHeight: 1.08, margin: '0 0 24px', opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(24px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.15s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.15s' }}>
            {isRTL ? 'أنر طريقك نحو ' : 'Illuminate your path to '}
            <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
              {isRTL ? 'إتقان ITIL' : 'ITIL mastery'}
            </span>
          </h1>

          <p style={{ fontFamily: S.font, fontSize: 17, lineHeight: 1.85, color: 'var(--soleil-sub)', maxWidth: 520, margin: '0 auto 40px', opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(20px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.3s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.3s' }}>
            {t('home.hero.subtitle')}
          </p>

          <div className="soleil-hero-buttons" style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(18px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.45s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.45s' }}>
            <Link to="/courses" style={{
              fontFamily: S.font, fontSize: 14, fontWeight: 600, color: 'var(--soleil-dark)',
              background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
              border: 'none', borderRadius: 12, padding: '16px 34px', cursor: 'pointer',
              boxShadow: '0 4px 24px rgba(184,148,58,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
              display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none',
            }}>
              {t('home.hero.cta')} <Ic type="arrow" size={16} color="var(--soleil-dark)" />
            </Link>
            <Link to="/about" style={{
              fontFamily: S.font, fontSize: 14, fontWeight: 500, color: 'var(--soleil-sub)',
              background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 12,
              padding: '16px 28px', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none',
            }}>
              <Ic type="play" size={18} color="var(--soleil-sub)" /> {t('home.hero.ctaSecondary')}
            </Link>
          </div>
          {/* Quiz teaser */}
          <div style={{ marginTop: 28, opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(16px)', transition: 'opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.6s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.6s' }}>
            <a href="#cert-quiz" onClick={(e) => { e.preventDefault(); document.getElementById('cert-quiz')?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{ fontFamily: S.font, fontSize: 14, fontWeight: 600, color: 'var(--soleil-gold-dark)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, padding: '12px 28px', background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 50, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', transition: 'all 0.2s', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <Ic type="help" size={18} color="var(--soleil-mut)" />
              {isRTL ? 'اختبر معلوماتك في ITIL — جرّب الاختبار السريع' : 'Test your ITIL knowledge — try the quick quiz'}
              <span style={{ fontSize: 12, color: 'var(--soleil-mut)' }}>↓</span>
            </a>
          </div>


        </div>
      </section>

      {/* ═══ TRUST BAR ═══ */}
      <section style={{ background: 'var(--soleil-cream)', borderTop: '1px solid var(--soleil-sand)', borderBottom: '1px solid var(--soleil-sand)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px' }}>
          <div className="soleil-grid-trust" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {[
              ['500+', isRTL ? 'متخصص تم تدريبهم' : 'Professionals Trained', 'users'],
              ['98%', isRTL ? 'نسبة النجاح من المحاولة الأولى' : 'First-Attempt Pass Rate', 'target'],
              ['4.9', isRTL ? 'تقييم الطلاب' : 'Student Rating', 'star'],
            ].map(([v, l, ic], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '8px 0', borderRight: i < 2 ? '1px solid var(--soleil-sand)' : 'none' }}>
                <Ic type={ic} size={28} color="var(--soleil-gold)" />
                <div>
                  <div style={{ fontFamily: S.heading, fontSize: 26, fontWeight: 600, color: 'var(--soleil-gold)' }}>{v}</div>
                  <div style={{ fontFamily: S.font, fontSize: 11, color: 'var(--soleil-mut)' }}>{l}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURED COURSE ═══ */}
      <section style={{ padding: '80px 24px', background: 'var(--soleil-cream)' }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-sienna)', letterSpacing: '0.2em', marginBottom: 14 }}>
              {isRTL ? 'الدورة الرئيسية' : 'FLAGSHIP COURSE'}
            </div>
            <h2 style={{ fontFamily: S.heading, fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 600, color: 'var(--soleil-ink)' }}>
              {isRTL ? 'ابدأ مع ' : 'Start with the '}
              <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
                {isRTL ? 'الأساس' : 'foundation'}
              </span>
            </h2>
          </div>

          <div className="soleil-grid-course" style={{
            background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 22,
            display: 'grid', gridTemplateColumns: '1.3fr 1fr', overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(46,42,38,0.06)', position: 'relative',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--soleil-gold), var(--soleil-sienna))' }} />

            {/* Left — course info */}
            <div style={{ padding: '40px 36px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <IcBox type="book" color="var(--soleil-gold)" bg="var(--soleil-gold-pale)" size={44} />
                <div>
                  <div style={{ fontFamily: S.font, fontSize: 10, fontWeight: 700, color: 'var(--soleil-sienna)', letterSpacing: '0.12em' }}>
                    {isRTL ? 'شهادة ITIL 5' : 'ITIL 5 CERTIFICATION'}
                  </div>
                  <div style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: 'var(--soleil-ink)' }}>
                    {featuredCourse ? (isRTL ? featuredCourse.titleAr : featuredCourse.titleEn) : 'ITIL 5 Foundation'}
                  </div>
                </div>
              </div>

              <p style={{ fontFamily: S.font, fontSize: 14.5, color: 'var(--soleil-sub)', lineHeight: 1.75, marginBottom: 28 }}>
                {featuredCourse
                  ? (isRTL ? featuredCourse.shortDescriptionAr : featuredCourse.shortDescriptionEn)
                  : 'Complete certification preparation — master digital strategy, value co-creation, the Service Value System, and all exam topics with real-world examples.'}
              </p>
              {featuredCourse ? (
                <>
                  <div className="soleil-course-meta" style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
                    {[
                      [String(featuredCourse.totalLessons || '21'), isRTL ? 'درس' : 'Lessons'],
                      [`${featuredCourse.durationHours || 12}h`, isRTL ? 'محتوى' : 'Content'],
                      ['∞', isRTL ? 'وصول' : 'Access'],
                    ].map(([v, l], i) => (
                      <div key={i} style={{ background: 'var(--soleil-cream)', border: '1px solid var(--soleil-sand)', borderRadius: 11, padding: '14px 20px', flex: 1, textAlign: 'center' }}>
                        <div style={{ fontFamily: S.heading, fontSize: 22, fontWeight: 600, color: 'var(--soleil-ink)' }}>{v}</div>
                        <div style={{ fontFamily: S.font, fontSize: 10, color: 'var(--soleil-mut)', marginTop: 2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 20, borderTop: '1px solid var(--soleil-sand)' }}>
                    <span style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: 'var(--soleil-ink)' }}>
                      {formatPrice(featuredCourse.price, featuredCourse.currency)}
                    </span>
                    <Link
                      to={`/courses/${featuredCourse.slug}`}
                      style={{
                        fontFamily: S.font, fontSize: 14, fontWeight: 600, color: 'var(--soleil-dark)',
                        background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
                        border: 'none', borderRadius: 11, padding: '13px 28px', cursor: 'pointer',
                        boxShadow: '0 4px 16px rgba(184,148,58,0.25)',
                        display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none',
                      }}
                    >
                      {isRTL ? 'سجّل الآن' : 'Enroll now'} <Ic type="arrow" size={15} color="var(--soleil-dark)" />
                    </Link>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', paddingTop: 8 }}>
                  <div style={{
                    display: 'inline-block', padding: '8px 24px', borderRadius: 20,
                    background: 'transparent', border: '2px solid var(--soleil-gold)',
                    fontFamily: S.font, fontSize: 13, fontWeight: 700, color: 'var(--soleil-gold)',
                    letterSpacing: '0.15em',
                    marginBottom: 16,
                  }}>
                    {isRTL ? 'قريباً' : 'COMING SOON'}
                  </div>
                  <p style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)', margin: 0 }}>
                    {isRTL ? 'تواصل معنا للمزيد من المعلومات' : 'Contact us to be notified when we launch'}
                  </p>
                </div>
              )}

            </div>
            {/* Right — what's included */}
            <div className="soleil-course-card-right" style={{ background: 'var(--soleil-cream)', padding: '40px 32px', borderLeft: '1px solid var(--soleil-sand)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-gold-dark)', letterSpacing: '0.15em', marginBottom: 20 }}>
                {isRTL ? 'ما يتضمنه' : "WHAT'S INCLUDED"}
              </div>
              {[
                [isRTL ? 'وصول مدى الحياة للفيديوهات' : 'Lifetime video access', isRTL ? 'جميع الدروس متاحة للأبد على أي جهاز' : 'All lessons available forever, on any device'],
                [isRTL ? 'إعداد لامتحان PeopleCert' : 'PeopleCert exam prep', isRTL ? 'أسئلة تدريبية كاملة ومحاكاة للامتحان' : 'Full practice questions and exam simulation'],
                [isRTL ? 'شهادة إتمام' : 'Certificate of completion', isRTL ? 'شهادة PDF احترافية بمعرف فريد' : 'Professional PDF certificate with unique ID'],
                [isRTL ? 'دعم عربي وإنجليزي' : 'Arabic & English support', isRTL ? 'محتوى وفريق ثنائي اللغة في بغداد' : 'Bilingual content and team based in Baghdad'],
              ].map(([title, desc], i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '14px 0', borderBottom: i < 3 ? '1px solid var(--soleil-sand)' : 'none' }}>
                  <div style={{ marginTop: 2 }}><Ic type="check" size={16} color="var(--soleil-olive)" /></div>
                  <div>
                    <div style={{ fontFamily: S.font, fontSize: 13.5, fontWeight: 600, color: 'var(--soleil-ink)' }}>{title}</div>
                    <div style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-mut)', marginTop: 2, lineHeight: 1.5 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section style={{ padding: '88px 24px', background: 'var(--soleil-cream)', borderTop: '1px solid var(--soleil-sand)' }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-sienna)', letterSpacing: '0.2em', marginBottom: 14 }}>
              {isRTL ? 'لماذا ITIL Shift' : 'WHY ITIL SHIFT'}
            </div>
            <h2 style={{ fontFamily: S.heading, fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 600, color: 'var(--soleil-ink)' }}>
              {isRTL ? <>كل شيء مصمم من أجل <span style={{ color: 'var(--soleil-gold)' }}>نجاحك</span></> : <>Everything crafted for <span style={{ color: 'var(--soleil-gold)' }}>your success</span></>}
            </h2>
          </div>
          <div className="soleil-grid-features" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            {([
              ['grad', t('home.features.expertLed.title'), t('home.features.expertLed.description'), 'var(--soleil-gold)', 'var(--soleil-gold-pale)'],
              ['clock', t('home.features.flexible.title'), t('home.features.flexible.description'), 'var(--soleil-sienna)', 'var(--soleil-sienna-pale)'],
              ['clip', t('home.features.certification.title'), t('home.features.certification.description'), 'var(--soleil-olive)', 'var(--soleil-olive-pale)'],
              ['chat', t('home.features.support.title'), t('home.features.support.description'), 'var(--soleil-gold-dark)', 'var(--soleil-gold-pale)'],
            ] as [string, string, string, string, string][]).map(([ic, title, desc, color, bg], i) => (
              <div key={i} className="feat-animate" style={{ background: '#fff', borderRadius: 16, border: '1px solid var(--soleil-sand)', padding: '24px 20px', position: 'relative', overflow: 'hidden', opacity: 0, transform: 'translateY(28px)', transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s` }}>
                <div className="feat-bar" style={{ position: 'absolute', top: 0, left: '50%', width: 0, height: 2.5, background: color, opacity: 0.65, transform: 'translateX(-50%)', transition: `width 0.8s cubic-bezier(0.16,1,0.3,1) ${0.3 + i * 0.12}s` }} />
                <IcBox type={ic} color={color} bg={bg} size={44} />
                <h3 style={{ fontFamily: S.font, fontSize: 14, fontWeight: 700, color: 'var(--soleil-ink)', margin: '16px 0 6px' }}>{title}</h3>
                <p style={{ fontFamily: S.font, fontSize: 12.5, color: 'var(--soleil-sub)', lineHeight: 1.65, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CERTIFICATION QUIZ ═══ */}
      <div id="cert-quiz">
      <CertQuiz isRTL={isRTL} S={S} />
      </div>

      {/* ═══ TESTIMONIALS ═══ */}
      <section style={{ padding: '56px 24px', background: 'var(--soleil-linen)', borderTop: '1px solid var(--soleil-sand)', borderBottom: '1px solid var(--soleil-sand)' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{ fontFamily: S.font, fontSize: 10, fontWeight: 700, color: 'var(--soleil-gold)', letterSpacing: '0.18em', textAlign: 'center', marginBottom: 10 }}>
            {isRTL ? 'آراء الطلاب' : 'STUDENT REVIEWS'}
          </div>
          <h2 style={{ fontFamily: S.heading, fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 600, textAlign: 'center', marginBottom: 28, color: 'var(--soleil-ink)' }}>
            {isRTL ? 'ماذا يقول خريجونا' : 'What our graduates say'}
          </h2>
          <div className="soleil-grid-testimonials" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
            {([
              ['AB', isRTL ? 'ITIL Shift أعطتني كل ما أحتاجه للنجاح من المحاولة الأولى. مواضيع معقدة أصبحت بسيطة مع أمثلة واقعية.' : 'ITIL Shift gave me everything I needed to pass on my first attempt. Complex topics made simple with real-world examples.', 'Ahmed Al-Bayati', isRTL ? 'مدير خدمات تكنولوجيا المعلومات · بغداد' : 'IT Service Manager · Baghdad', 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))'],
              ['NM', isRTL ? 'دليل الدراسة والامتحانات التدريبية أعدّتني بشكل مثالي. منصة محلية بتدريب معتمد من بريطانيا.' : 'The study guide and practice exams prepared me perfectly. A local platform with UK-accredited training — no compromise on quality.', 'Noor Mohammed', isRTL ? 'قائد مكتب الخدمة · البصرة' : 'Service Desk Lead · Basra', 'linear-gradient(135deg, #1A5C3A, #2D7A50)'],
              ['OK', isRTL ? 'التركيز على ITIL 5 مع الذكاء الاصطناعي والتحول الرقمي يميز هذه المنصة. محتوى حديث وقابل للتطبيق.' : 'Focus on ITIL 5 with AI and digital transformation sets this apart. Content is modern and directly applicable to my daily work.', 'Omar Kareem', isRTL ? 'مستشار تقنية المعلومات · أربيل' : 'IT Consultant · Erbil', 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))'],
            ] as [string, string, string, string, string][]).map(([initials, quote, name, role, grad], i) => (
              <div key={i} className="test-animate" style={{ background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 14, padding: '20px 18px', opacity: 0, transform: 'translateY(28px)', transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${0.15 + i * 0.15}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${0.15 + i * 0.15}s` }}>
                <div style={{ display: 'flex', gap: 2, marginBottom: 10 }}>
                  {[...Array(5)].map((_, s) => (
                    <svg key={s} width="12" height="12" viewBox="0 0 24 24" fill="var(--soleil-gold)" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26"/></svg>
                  ))}
                </div>
                <p style={{ fontFamily: S.heading, fontSize: 12.5, color: 'var(--soleil-sub)', lineHeight: 1.7, fontStyle: 'italic', margin: '0 0 16px' }}>"{quote}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: grad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{initials}</div>
                  <div>
                    <div style={{ fontFamily: S.font, fontSize: 12, fontWeight: 700, color: 'var(--soleil-ink)' }}>{name}</div>
                    <div style={{ fontFamily: S.font, fontSize: 10, color: 'var(--soleil-mut)' }}>{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ padding: '96px 24px', background: 'var(--soleil-linen)', position: 'relative', overflow: 'hidden', borderTop: '1px solid var(--soleil-sand)' }}>
        <div style={{ maxWidth: 540, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <Ic type="sun" size={32} color="var(--soleil-gold)" />
          <h2 style={{ fontFamily: S.heading, fontSize: 'clamp(32px, 4vw, 46px)', fontWeight: 600, color: 'var(--soleil-ink)', lineHeight: 1.1, margin: '20px 0 16px' }}>
            {isRTL ? 'مستعد لـ' : 'Ready to '}
            <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
              {isRTL ? 'التألق؟' : 'shine?'}
            </span>
          </h2>
          <p style={{ fontFamily: S.font, fontSize: 15, color: 'var(--soleil-sub)', lineHeight: 1.75, marginBottom: 36 }}>
            {t('home.cta.subtitle')}
          </p>
          <div className="soleil-hero-buttons" style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/courses" style={{
              fontFamily: S.font, fontSize: 14, fontWeight: 600, color: 'var(--soleil-dark)',
              background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
              border: 'none', borderRadius: 12, padding: '16px 36px', cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(184,148,58,0.3)', textDecoration: 'none',
            }}>
              {isRTL ? 'ابدأ التعلم اليوم' : 'Start learning today'}
            </Link>
            <Link to="/contact" style={{
              fontFamily: S.font, fontSize: 14, fontWeight: 500, color: 'var(--soleil-sub)',
              background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 12,
              padding: '16px 28px', cursor: 'pointer', textDecoration: 'none',
            }}>
              {t('nav.contact')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
